import React, { useState, useEffect } from "react";
import {
  Container,
  Title,
  Text,
  Button,
  Table,
  Badge,
  Group,
  Stack,
  Loader,
  Paper,
  Box,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconPlus, IconEdit, IconTrash } from "@tabler/icons-react";
import apiService from "../services/api";
import AddEditCustomerPriceModal from "./AddEditCustomerPriceModal";
import { formatPrice } from '../utils/formatPrice';

export default function AdminCustomerPrices() {
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  const fetchPrices = async () => {
    setLoading(true);
    try {
      const data = await apiService.getCustomerPrices();
      setPrices(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrices();
  }, []);

  const handleDelete = (id) => {
    modals.openConfirmModal({
      title: "Delete Custom Price",
      centered: true,
      children: (
        <Text size="sm">
          Are you sure you want to delete this custom price?
        </Text>
      ),
      labels: { confirm: "Delete", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        try {
          await apiService.deleteCustomerPrice(id);
          fetchPrices();
        } catch (e) {
          console.error(e);
          modals.open({
            title: "Error",
            children: <Text size="sm">Failed to delete price</Text>,
          });
        }
      },
    });
  };

  if (loading)
    return (
      <Container py="xl">
        <Loader />
      </Container>
    );

  return (
    <Container fluid py="xl">
      <Group justify="space-between" mb="xl">
        <Stack gap={0}>
          <Title order={2}>Custom Pricing</Title>
          <Text c="dimmed">
            Manage specific custom product price points for volume clients.
          </Text>
        </Stack>          <Button
            leftSection={<IconPlus size={16} aria-hidden="true" />}
            onClick={() => {
              setEditData(null);
              setIsModalOpen(true);
            }}
          >
            Add Custom Prices
          </Button>
      </Group>

      <Paper withBorder p={0}>
        <caption className="sr-only">Custom pricing rules per customer</caption>
        <Table verticalSpacing="md" horizontalSpacing="md" highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th scope="col">Customer</Table.Th>
              <Table.Th scope="col">Product</Table.Th>
              <Table.Th scope="col">Pricing Config</Table.Th>
              <Table.Th scope="col">Start Date</Table.Th>
              <Table.Th scope="col">End Date</Table.Th>
              <Table.Th scope="col">Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {prices.map((price) => (
              <Table.Tr key={price.id}>
                <Table.Td>
                  <Text fw={500}>{price.user_name}</Text>
                  <Text size="xs" c="dimmed">
                    {price.user_email}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text fw={500}>{price.product_name}</Text>
                  <Text size="xs" c="dimmed">
                    (
                    {price.weight_value != null
                      ? `${price.weight_value}${price.weight_unit || ""} \u2013 `
                      : ""}
                    {price.variant_sku})
                  </Text>
                </Table.Td>
                <Table.Td>
                  {price.pricing_model === "volume" ? (
                    <Box>
                      <Badge color="blue" variant="light" mb={4}>
                        Volume
                      </Badge>
                      <Text size="sm">
                        Base: {formatPrice(price.price)}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {price.volume_type === "weight" ? "Weight" : "Qty"} &ge;{" "}
                        {price.volume_threshold}{" "}
                        {price.volume_type === "weight" ? "kg" : "units"} &rarr;
                        <Text span fw={700}>
                          {" "}
                          {formatPrice(price.post_threshold_price)}
                        </Text>
                      </Text>
                    </Box>
                  ) : (
                    <Box>
                      <Badge color="gray" variant="light" mb={4}>
                        Fixed
                      </Badge>
                      <Text size="sm">
                        {formatPrice(price.price)}
                      </Text>
                    </Box>
                  )}
                </Table.Td>
                <Table.Td>
                  {price.start_time
                    ? new Date(price.start_time).toLocaleString()
                    : "Permanent"}
                </Table.Td>
                <Table.Td>
                  {price.end_time
                    ? new Date(price.end_time).toLocaleString()
                    : "Never"}
                </Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    <Button
                      size="xs"
                      variant="default"
                      leftSection={<IconEdit size={14} aria-hidden="true" />}
                      onClick={() => {
                        setEditData(price);
                        setIsModalOpen(true);
                      }}
                      aria-label={`Edit custom price for ${price.user_name} on ${price.product_name}`}
                    >
                      Edit
                    </Button>
                    <Button
                      size="xs"
                      color="red"
                      leftSection={<IconTrash size={14} aria-hidden="true" />}
                      onClick={() => handleDelete(price.id)}
                      aria-label={`Delete custom price for ${price.user_name} on ${price.product_name}`}
                    >
                      Delete
                    </Button>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
            {prices.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={6}>
                  <Text ta="center" c="dimmed">
                    No custom prices configured.
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Paper>

      {isModalOpen && (
        <AddEditCustomerPriceModal
          editData={editData}
          onClose={() => {
            setIsModalOpen(false);
            setEditData(null);
          }}
          onSaved={fetchPrices}
        />
      )}
    </Container>
  );
}
